import type { EventBus } from '../EventBus';
import type { SessionStorageService } from './browser/SessionStorageService';
import type { TrackingDebugService } from './TrackingDebugService';
import { USER_IDLE_THRESHOLD_MS } from '../config/userIdle';
import { StorageKey } from '../config/storageKeys';
import { TrackingBusEvent } from '../constants/trackingBusEvent';
import { TrackingTag } from '../constants/trackingTag';

function nowMs(): number {
  return Date.now();
}

function uuidv4(): string {
  // Good enough for client-side identifiers; no crypto requirement here.
  // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;

    return v.toString(16);
  });
}

function parseIntSafe(value: string | null): number | null {
  if (value == null) {
    return null;
  }
  const n = Number.parseInt(value, 10);

  return Number.isFinite(n) ? n : null;
}

interface CheckInactivityOptions {
  /**
   * Default `true`. Set `false` when `HeartbeatLoop` handles idle rotation itself (timer idle gate or
   * pre-send hook) so we do not emit `tab:reset` from inside paths that would schedule another heartbeat.
   */
  emitTabReset?: boolean;
}

/**
 * Per-tab analytics identity: ids and timestamps live in sessionStorage (see `StorageKey`).
 * Idle detection uses `USER_IDLE_THRESHOLD_MS` (`config/userIdle.ts`). When `HeartbeatLoop` invokes
 * `checkInactivityAndResetIfNeeded({ emitTabReset: false })`, storage still rotates but no `tab:reset`
 * bus event fires — avoids nested heartbeat work from inside heartbeat paths.
 */
export class TabService {
  constructor(
    private readonly storage: SessionStorageService,
    private readonly bus: EventBus,
    private readonly debug?: TrackingDebugService,
  ) {}

  init(): void {
    const tabId = this.storage.get(StorageKey.TabId);
    const lastActive = parseIntSafe(this.storage.get(StorageKey.TabLastActive));
    const index = parseIntSafe(this.storage.get(StorageKey.HeartbeatIndex));

    if (!tabId || lastActive == null || index == null) {
      this.debug?.log('TabService', 'init: missing keys → resetTab', {});
      this.resetTab();
    }

    const onActivity = () => {
      // Compare elapsed time against the *previous* last_active first; recording activity
      // before the check would refresh the timestamp and make inactivity impossible to detect.
      this.checkInactivityAndResetIfNeeded();
      this.recordActivity();
    };

    // `tab:active` is emitted when the tab becomes visible again.
    // `tab:activity` is emitted on user interaction while visible.
    this.bus.subscribe(TrackingBusEvent.TabActive, onActivity, TrackingTag.TabService);
    this.bus.subscribe(TrackingBusEvent.TabActivity, onActivity, TrackingTag.TabService);

    this.bus.subscribe(
      TrackingBusEvent.PurchaseCompleted,
      () => {
        this.debug?.log('TabService', 'purchase:completed → reset tab', {});
        this.resetTab();
        this.bus.emit(TrackingBusEvent.TabReset, { tabId: this.getTabId() }, { publisher: TrackingTag.TabService });
      },
      TrackingTag.TabService,
    );

    this.bus.subscribe(
      TrackingBusEvent.ConsentWithdrawn,
      () => {
        this.clearStorage();
      },
      TrackingTag.TabService,
    );
  }

  getTabId(): string {
    const tabId = this.storage.get(StorageKey.TabId);
    if (!tabId) {
      const newId = uuidv4();
      this.storage.set(StorageKey.TabId, newId);

      return newId;
    }

    return tabId;
  }

  recordActivity(): void {
    this.storage.set(StorageKey.TabLastActive, String(nowMs()));
  }

  /** True when last activity timestamp is older than USER_IDLE_THRESHOLD_MS. */
  isBeyondIdleThreshold(): boolean {
    const lastActive = parseIntSafe(this.storage.get(StorageKey.TabLastActive));
    if (lastActive == null) {
      return false;
    }

    return nowMs() - lastActive > USER_IDLE_THRESHOLD_MS;
  }

  checkInactivityAndResetIfNeeded(options?: CheckInactivityOptions): void {
    const lastActive = parseIntSafe(this.storage.get(StorageKey.TabLastActive));
    if (lastActive == null) {
      return;
    }

    const diff = nowMs() - lastActive;
    if (diff <= USER_IDLE_THRESHOLD_MS) {
      return;
    }

    this.debug?.log('TabService', 'inactivity threshold exceeded → reset', {
      inactiveMs: diff,
      thresholdMs: USER_IDLE_THRESHOLD_MS,
    });
    this.resetTab();
    const emitTabReset = options?.emitTabReset !== false;
    if (emitTabReset) {
      this.bus.emit(TrackingBusEvent.TabReset, { tabId: this.getTabId() }, { publisher: TrackingTag.TabService });
    }
  }

  getNextIndex(): number {
    // Post-increment: return current value, then increment & persist.
    const current = parseIntSafe(this.storage.get(StorageKey.HeartbeatIndex)) ?? 0;
    this.storage.set(StorageKey.HeartbeatIndex, String(current + 1));

    return current;
  }

  /**
   * Resets the per-tab heartbeat index to 0 when the viewed product changes, so downstream
   * aggregation sees a per-product beat sequence. Resets the **index only** — `tab_id` is left
   * intact (tab rotation is reserved for idle / purchase / consent). A revisit to an earlier
   * product is therefore a fresh view starting at 0. No-op for a falsy id (non-PDP page) or when
   * the product is unchanged (e.g. a same-product reload, which should continue the sequence).
   */
  resetIndexOnProductChange(productId: string): void {
    if (!productId) {
      return;
    }
    if (this.storage.get(StorageKey.LastProductId) === productId) {
      return;
    }
    this.storage.set(StorageKey.LastProductId, productId);
    this.storage.set(StorageKey.HeartbeatIndex, '0');
  }

  private resetTab(): void {
    this.storage.set(StorageKey.TabId, uuidv4());
    this.storage.set(StorageKey.TabLastActive, String(nowMs()));
    this.storage.set(StorageKey.HeartbeatIndex, '0');
  }

  private clearStorage(): void {
    this.storage.remove(StorageKey.TabId);
    this.storage.remove(StorageKey.TabLastActive);
    this.storage.remove(StorageKey.HeartbeatIndex);
    this.storage.remove(StorageKey.LastProductId);
  }
}
